/**
 * Sponsor Discovery effects - side effect handlers.
 * 
 * Elm-ish architecture: Side effects are isolated here.
 * The effect runner (useSponsorDiscoveryProgram.ts) calls these functions.
 * 
 * Security posture:
 * - All API calls use identity-minimized endpoints
 * - No credentials logged
 * - Safe error messages only
 * - Endpoint selection by registration kind (server-side enforcement)
 */
import { listDiscoveryProfiles, ProfileError } from '@/api/profiles';
import { submitIntroRequest } from '@/api/introRequests';
import { useAuthStore } from '@/store/authStore';
import type { SponsorDiscoveryEffect } from './update';

/**
 * Effect handler context - injected by the effect runner.
 */
export interface EffectContext {
  dispatch: (msg: import('./messages').SponsorDiscoveryMsg) => void;
}

/**
 * Run a single effect.
 * Returns a cleanup function if needed.
 */
export async function runEffect(
  effect: SponsorDiscoveryEffect,
  context: EffectContext
): Promise<(() => void) | void> {
  switch (effect.type) {
    case 'LoadCards':
      return runLoadCards(context);
      
    case 'SkipCard':
      // Skip is currently a no-op on backend - just advance to next card
      context.dispatch({ type: 'ActionSucceeded' });
      return;
      
    case 'SaveCard':
      // Save is currently a no-op on backend - just advance to next card
      context.dispatch({ type: 'ActionSucceeded' });
      return;
      
    case 'RequestIntroCard':
      return runRequestIntroCard(context, effect.cardId);
      
    case 'LoadMoreCards':
      return runLoadMoreCards(context, effect.offset, effect.limit);
      
    default:
      const _exhaustive: never = effect;
      return;
  }
}

/**
 * Load initial cards from the API.
 * Selects endpoint based on registration kind.
 */
async function runLoadCards(context: EffectContext): Promise<void> {
  try {
    const registrationKind = useAuthStore.getState().registrationKind;
    const response = await listDiscoveryProfiles(registrationKind, { limit: 20, offset: 0 });
    context.dispatch({
      type: 'LoadSucceeded',
      cards: response.profiles,
    });
  } catch (err) {
    if (err instanceof ProfileError) {
      // 403 means this page is not available for this account type
      if (err.statusCode === 403) {
        context.dispatch({
          type: 'LoadFailed',
          error: 'This page is not available for your account type',
        });
        return;
      }
      const errorMessage = err.safe ? 'Discovery service unavailable' : 'Failed to load profiles';
      context.dispatch({
        type: 'LoadFailed',
        error: errorMessage,
      });
    } else {
      context.dispatch({
        type: 'LoadFailed',
        error: 'Discovery service unavailable',
      });
    }
  }
}

/**
 * Load more cards (pagination).
 * Selects endpoint based on registration kind.
 */
async function runLoadMoreCards(context: EffectContext, offset: number, limit: number): Promise<void> {
  try {
    const registrationKind = useAuthStore.getState().registrationKind;
    const response = await listDiscoveryProfiles(registrationKind, { limit, offset });
    context.dispatch({
      type: 'LoadMoreSucceeded',
      cards: response.profiles,
    });
  } catch (err) {
    if (err instanceof ProfileError) {
      // 403 means this page is not available for this account type
      if (err.statusCode === 403) {
        context.dispatch({
          type: 'LoadMoreFailed',
          error: 'This page is not available for your account type',
        });
        return;
      }
      const errorMessage = err.safe ? 'Discovery service unavailable' : 'Failed to load more';
      context.dispatch({
        type: 'LoadMoreFailed',
        error: errorMessage,
      });
    } else {
      context.dispatch({
        type: 'LoadMoreFailed',
        error: 'Discovery service unavailable',
      });
    }
  }
}

/**
 * Submit intro request to the backend API.
 * Dispatches ActionSucceeded on success, ActionFailed on error.
 * 
 * Security notes:
 * - sponsor_id is NOT sent; backend derives it from authenticated session
 * - Only card_id, message, and acknowledged fields are sent
 * - Backend validates and emits audit events
 */
async function runRequestIntroCard(context: EffectContext, cardId: string): Promise<void> {
  try {
    await submitIntroRequest({
      cardId,
      acknowledged: true,
    });
    context.dispatch({ type: 'ActionSucceeded' });
  } catch (err) {
    // Extract error message from thrown Error
    const errorMessage = err instanceof Error ? err.message : 'Failed to submit intro request';
    context.dispatch({ type: 'ActionFailed', error: errorMessage });
  }
}

/**
 * Create an effect for advancing after a successful action.
 * This is a synchronous effect (no API call).
 */
export function createAdvanceEffect(): SponsorDiscoveryEffect | null {
  // The ActionSucceeded message is dispatched synchronously
  // when the UI component receives the action callback
  return null; // No async effect needed for local advance
}
